import { useMemo, useState, useEffect } from "react"
import { HugeiconsIcon } from "@hugeicons/react"
import { ArrowRight01Icon, FlashIcon } from "@hugeicons/core-free-icons"
import { Button } from "@/components/ui/button"
import { InlineNotice } from "@/components/shared/layout"
import { cn } from "@/lib/utils"
import {
  pageFactsFrom,
  screenshotStateFor,
  type PageAnalysisRecommendation,
  type PageAnalysisResult,
  type ScreenshotState,
} from "@/lib/api/onboardingTests"
import { goalNoun, type OnboardingGoalId } from "@/lib/types/onboarding-goal"
import { successLooksLike } from "@/lib/config/onboardingHeuristics"
import { OnboardingTestCard } from "./OnboardingTestCard"
import { OnboardingExampleTests } from "./OnboardingExampleTests"
import { OnboardingResearchStatus } from "./OnboardingResearchStatus"
import { useOnboardingSnapshot } from "./useOnboardingSnapshot"

/** What the page needs for `onboarding_plan_cta_clicked`. */
export type PlanCtaMeta = {
  heuristic: boolean
  screenshot_state: ScreenshotState
  seconds_on_step: number | null
}

type OnboardingPlanRecsProps = {
  recs: PageAnalysisRecommendation[]
  goal: OnboardingGoalId | null
  heuristic: boolean
  /**
   * Label for the primary action. The plan step now exits to the tracker
   * ("Install to run these"), not to the dashboard.
   */
  continueLabel?: string
  /** Receives the event payload for `onboarding_plan_cta_clicked`. */
  onContinue: (meta: PlanCtaMeta) => void
  /** Reports the polled site-snapshot URL so the page can record the same `screenshot_state`. */
  onSnapshotUrlChange?: (url: string | null) => void
  /** Class on the inline examples block. The page hides it at lg, where the examples sit in their own column. */
  examplesClassName?: string
  onOpenTest?: (rec: PageAnalysisRecommendation) => void
  onBookCall: () => void
  /** Optional: enables the live page-screenshot poll. Without both, the cards stay on the skeleton. */
  propertyId?: string | null
  token?: string | null
  /** Optional: labels the A/B alt text with the page under test. */
  domain?: string | null
  /** Optional: Firecrawl before/after shots from the generation job, when it captured them. */
  controlImageUrl?: string | null
  variantImageUrl?: string | null
  /** The crawl's own state, so the plan can say what it is drafted from. */
  researchState?: "pending" | "done" | "failed"
  /** Time on this step, forwarded into the CTA event. */
  secondsOnStep?: number
  /** The crawl result. Feeds the facts fallback when there is no screenshot. */
  analysis?: PageAnalysisResult | null
}

export function OnboardingPlanRecs({
  recs,
  goal,
  heuristic,
  continueLabel = "Install to run these",
  onContinue,
  onSnapshotUrlChange,
  examplesClassName,
  onOpenTest,
  onBookCall,
  propertyId,
  token,
  domain,
  controlImageUrl,
  variantImageUrl,
  researchState,
  secondsOnStep,
  analysis,
}: OnboardingPlanRecsProps) {
  const noun = goalNoun(goal)
  const lead = recs[0]
  const [showThird, setShowThird] = useState(false)

  // The crawl can lag this step, so the cards ship a skeleton and swap the real shot in.
  const snapshotUrl = useOnboardingSnapshot({
    propertyId,
    token,
    domain,
    enabled: !controlImageUrl,
  })
  const controlUrl = controlImageUrl ?? snapshotUrl
  useEffect(() => {
    onSnapshotUrlChange?.(snapshotUrl)
  }, [snapshotUrl, onSnapshotUrlChange])
  const screenshotState = useMemo(
    () => screenshotStateFor({ firecrawlUrl: controlImageUrl, snapshotUrl }, analysis),
    [controlImageUrl, snapshotUrl, analysis],
  )
  /** Quoted crawl facts, used only while no screenshot has landed. */
  const facts = useMemo(
    () => (screenshotState === "facts" ? pageFactsFrom(analysis) : null),
    [screenshotState, analysis],
  )

  const pending = researchState === "pending"
  const failed = researchState === "failed"
  const visible = recs.slice(0, showThird ? 3 : 2)

  return (
    <div className="space-y-5">
      {pending ? (
        <OnboardingResearchStatus
          variant="strip"
          domain={domain || "your site"}
          phase="reading"
        />
      ) : null}

      {failed ? (
        <InlineNotice
          variant="warning"
          message="We could not read your site."
          description="These tests are drafted from the goal you picked. You can still install and run them."
        />
      ) : null}

      <p className="text-xs leading-snug text-muted-foreground">
        {successLooksLike(goal, lead?.title)}
      </p>

      <div className="space-y-2.5">
        {visible.map((rec, i) => (
          <OnboardingTestCard
            key={rec.id}
            rec={rec}
            index={i}
            controlImageUrl={controlUrl}
            // The generation job's after-shot has the lead recommendation
            // applied, so only the first card may use it; the others get the
            // highlight overlay on the control.
            variantImageUrl={i === 0 ? variantImageUrl : null}
            facts={facts}
            pageLabel={domain ?? undefined}
            onOpenTest={onOpenTest ? () => onOpenTest(rec) : undefined}
          />
        ))}

        {recs.length > 2 && !showThird ? (
          <button
            type="button"
            onClick={() => setShowThird(true)}
            className="text-[11px] text-foreground/80 underline-offset-2 hover:text-foreground hover:underline"
          >
            See one more
          </button>
        ) : null}

        {pending || heuristic ? (
          <p className="text-[11px] text-muted-foreground">
            Drafted from your goal; they refine when the crawl lands.
          </p>
        ) : null}
      </div>

      <OnboardingExampleTests goal={goal} pending={pending} headingId="onboarding-examples-heading-inline" className={examplesClassName} />

      <Button
        type="button"
        className="h-9 w-full gap-2"
        onClick={() =>
          onContinue({
            heuristic,
            screenshot_state: screenshotState,
            seconds_on_step: typeof secondsOnStep === "number" ? secondsOnStep : null,
          })
        }
      >
        {continueLabel}
        <HugeiconsIcon icon={ArrowRight01Icon} size={14} />
      </Button>
      <button
        type="button"
        onClick={onBookCall}
        className={cn(
          "flex w-full items-center justify-center gap-1.5 text-xs text-muted-foreground hover:text-foreground",
        )}
      >
        <HugeiconsIcon icon={FlashIcon} size={12} />
        Prefer a 30-minute setup call instead?
      </button>
      <p className="text-center text-[11px] text-muted-foreground">
        Launching a test is optional. Connecting the tracker is what starts measuring {noun}.
      </p>
    </div>
  )
}
